import { ThemedText } from '@/components/themed-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/utils/format-currency';
import React from 'react';
import { View, useWindowDimensions } from 'react-native';
import {
  buildXLabels,
  formatCurrencyAxis,
  groupByPeriod,
  type ChartPeriod,
} from './chart/chart-utils';
import { LineAreaChart } from './chart/line-area-chart';

export type ComparativeDataPoint = {
  date: string;
  revenue: number;
  expenses: number;
  profit: number;
};

type ComparativeRevenueChartProps = {
  data?: ComparativeDataPoint[];
  period?: ChartPeriod;
  isLoading?: boolean;
};

const REVENUE_COLOR = '#FD5001'; // primary
const EXPENSES_COLOR = '#DC2626'; // error-600
const PROFIT_COLOR = '#16A34A'; // success-600

const TITLE = 'Recebidos x Gastos x Lucro';

export function ComparativeRevenueChart({
  data = [],
  period = '30d',
  isLoading = false,
}: ComparativeRevenueChartProps) {
  const { height } = useWindowDimensions();
  const chartHeight = Math.max(200, Math.min(400, height * 0.35));

  if (isLoading || data.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            <ThemedText className="font-semibold text-xl">{TITLE}</ThemedText>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <View style={{ height: chartHeight }} className="items-center justify-center">
            <ThemedText className="text-foreground/60 text-sm">
              {isLoading ? 'Carregando...' : 'Nenhum dado no período'}
            </ThemedText>
          </View>
        </CardContent>
      </Card>
    );
  }

  const grouped = groupByPeriod(data, period, ['revenue', 'expenses', 'profit']);
  const xLabels = buildXLabels(grouped.map((d) => d.date));

  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const totalExpenses = data.reduce((s, d) => s + d.expenses, 0);
  const totalProfit = data.reduce((s, d) => s + d.profit, 0);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          <ThemedText className="font-semibold text-xl">{TITLE}</ThemedText>
        </CardTitle>
        <View className="mt-2 flex-row flex-wrap gap-4">
          <LegendItem color={REVENUE_COLOR} label="Recebidos" value={totalRevenue} />
          <LegendItem color={EXPENSES_COLOR} label="Gastos" value={totalExpenses} />
          <LegendItem color={PROFIT_COLOR} label="Lucro" value={totalProfit} />
        </View>
      </CardHeader>
      <CardContent>
        <LineAreaChart
          series={[
            {
              values: grouped.map((d) => d.revenue),
              color: REVENUE_COLOR,
              showArea: true,
              showEndDot: true,
              strokeWidth: 3,
            },
            {
              values: grouped.map((d) => d.expenses),
              color: EXPENSES_COLOR,
              showEndDot: true,
              strokeWidth: 2.5,
            },
            {
              values: grouped.map((d) => d.profit),
              color: PROFIT_COLOR,
              showEndDot: true,
              strokeWidth: 2.5,
            },
          ]}
          xLabels={xLabels}
          height={chartHeight}
          formatY={formatCurrencyAxis}
        />
      </CardContent>
    </Card>
  );
}

function LegendItem({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) {
  return (
    <View className="flex-row items-center gap-2">
      <View className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
      <ThemedText className="text-foreground/80 text-sm">
        {label}: {formatCurrency(value)}
      </ThemedText>
    </View>
  );
}
